/**
 * useGamification Hook
 * Manages gamification state and provides methods to interact with the system
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserProgressSummary,
  awardXP,
} from '../services/firebase/gamification';
import { migrateUserGamification } from '../services/firebase/migrationService';
import { DEFAULT_TASKS } from '../types/gamification';


interface GamificationProgress {
  totalXP: number;
  readinessPercentage: number;
  completedTasks: string[];
  totalTasks: number;
  level: {
    min: number;
    max: number;
    color: string;
    label: string;
    emoji: string;
  };
  nextTask: {
    id: string;
    title: string;
    description: string;
    xpReward: number;
  } | null;
}

export const useGamification = () => {
  const { user } = useAuth();
  const [progress, setProgress] = useState<GamificationProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's gamification progress
  const fetchProgress = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let summary = await getUserProgressSummary(user.uid);
      
      // If user has no gamification data (0 XP and no tasks), try to migrate
      if (summary.totalXP === 0 && summary.completedTasks.length === 0) {
        try {
          await migrateUserGamification(user.uid);
          
          // Fetch again after migration
          summary = await getUserProgressSummary(user.uid);
        } catch (migrationError) {
          // Migration failed, continue with default data
          console.error('Migration failed:', migrationError);
        }
      }
      
      setProgress(summary);
      setError(null);
    } catch (err) {
      console.error('Error fetching gamification progress:', err);
      setError('Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Award XP for completing a task
  const awardTaskXP = useCallback(
    async (taskId: string) => {
      if (!user) {
        throw new Error('User must be authenticated');
      }

      try {
        // Find the task to get XP amount
        const task = DEFAULT_TASKS.find(t => t.id === taskId);
        if (!task) {
          throw new Error(`Task ${taskId} not found`);
        }

        // Award XP using client-side function
        await awardXP(user.uid, taskId, task.xpReward);
        
        // Refresh progress after awarding XP
        await fetchProgress();
        
        return {
          success: true,
          xpAwarded: task.xpReward,
        };
      } catch (err) {
        console.error('Error awarding XP:', err);
        throw new Error('Failed to award XP');
      }
    },
    [user, fetchProgress]
  );

  // Check if a task is completed
  const isTaskCompleted = useCallback(
    (taskId: string): boolean => {
      return progress?.completedTasks.includes(taskId) || false;
    },
    [progress]
  );

  // Load progress on mount and when user changes
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Listen for gamification updates from other components
  useEffect(() => {
    const handleGamificationUpdate = () => {
      // eslint-disable-next-line no-console
      console.log('[useGamification] Received gamification update event, refreshing...');
      fetchProgress();
    };

    window.addEventListener('gamificationUpdate', handleGamificationUpdate);
    return () => window.removeEventListener('gamificationUpdate', handleGamificationUpdate);
  }, [fetchProgress]);

  return {
    progress,
    loading,
    error,
    fetchProgress,
    awardTaskXP,
    isTaskCompleted,
  };
};

export default useGamification;
